import type { HTMLAttributes } from "react";
import {
  Breadcrumb as ReactAriaBreadcrumb,
  Breadcrumbs as ReactAriaBreadcrumbs,
  type BreadcrumbProps as ReactAriaBreadcrumbProps,
  type BreadcrumbsProps as ReactAriaBreadcrumbsProps,
} from "react-aria-components";

import { cn, cv, type VariantProps } from "../utils";

const ncBreadcrumbsVariants = cv("bg-ui-light border-ui border-b print:hidden", {
  variants: {
    variant: {
      default: "",
      sticky: "sticky top-0 z-1",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

interface NcBreadcrumbsProps
  extends HTMLAttributes<HTMLElement>, VariantProps<typeof ncBreadcrumbsVariants> {
  ariaLabel: string;
}

const NcBreadcrumbs = ({ variant, ariaLabel, className, ...props }: NcBreadcrumbsProps) => {
  return (
    <nav
      data-nc="NcBreadcrumbs"
      className={cn(ncBreadcrumbsVariants({ variant }), className)}
      aria-label={ariaLabel}
      {...props}
    />
  );
};

const Breadcrumbs = function <T extends object>({
  children,
  className,
  ...props
}: ReactAriaBreadcrumbsProps<T>) {
  return (
    <ReactAriaBreadcrumbs
      data-nc="NcBreadcrumbs.Breadcrumbs"
      className={cn("mx-auto flex min-h-11 max-w-400 items-center px-8 py-2", className)}
      {...props}
    >
      {children}
    </ReactAriaBreadcrumbs>
  );
};

const Breadcrumb = ({ children, className, ...props }: ReactAriaBreadcrumbProps) => {
  return (
    <ReactAriaBreadcrumb
      data-nc="NcBreadcrumbs.Breadcrumb"
      className={cn(
        "text-main relative after:px-1.5 after:content-['/'] last:after:content-['']",
        className
      )}
      {...props}
    >
      {children}
    </ReactAriaBreadcrumb>
  );
};

NcBreadcrumbs.Breadcrumbs = Breadcrumbs;
NcBreadcrumbs.Breadcrumb = Breadcrumb;

export { NcBreadcrumbs };
